import ExcelJS from "exceljs";
import { slugifyTitle } from "./productUrlUtils";

export const exportToExcel = async (data, columns, filename = 'export') => {
  try {
    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Orders');
    
    // Add headers with styling
    const headers = columns.map(col => col.label);
    const headerRow = worksheet.addRow(headers);
    
    // Style the header row
    headerRow.eachCell((cell, colNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      cell.font = {
        color: { argb: 'FFFFFFFF' },
        bold: true,
        size: 12
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center'
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    
    // Add data rows
    data.forEach((row, rowIndex) => {
      const rowData = columns.map(col => {
        let value = row[col.key];
        
        // Handle special cases for rendered content
        if (col.key === 'totalPrice' || col.key === 'mkp_current_total_price' || 
            col.key === 'mkp_current_received_price' || col.key === 'mkp_current_outstanding_price') {
          return parseFloat(value || 0);
        } else if (col.key === 'orderDate') {
          return new Date(value);
        } else if (col.key === 'totalQuantity') {
          return `${value} unit`;
        } else if (col.key === 'orderItemId') {
          return row.orderDetails?.[0]?.orderItemId || '';
        }
        
        return value || '';
      });
      
      const dataRow = worksheet.addRow(rowData);
      
      // Style data rows
      dataRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF8F9FA' }
          };
        }
        
        // Format currency columns
        const column = columns[colNumber - 1];
        if (column && (column.key === 'totalPrice' || column.key === 'mkp_current_total_price' || 
            column.key === 'mkp_current_received_price' || column.key === 'mkp_current_outstanding_price')) {
          cell.numFmt = '₹#,##0.00';
        }
        
        // Format date columns
        if (column && column.key === 'orderDate') {
          cell.numFmt = 'dd/mm/yyyy hh:mm';
        }
      });
    });
    
    // Auto-fit columns
    columns.forEach((col, index) => {
      const column = worksheet.getColumn(index + 1);
      column.width = Math.max(col.label.length + 2, col.width ? col.width / 8 : 15);
    });
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = `${filename}-${timestamp}.xlsx`;
    
    // Generate buffer and create download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', finalFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename: finalFilename };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error.message };
  }
};

export const exportTableToExcel = async (tableData, tableColumns, filename = 'table-export') => {
  return await exportToExcel(tableData, tableColumns, filename);
};

/** Per-row product/variant column keys - one row per variant (includes breakdown + variant wholesale fields) */
const ROW_ITEM_KEYS = [
  "productImageLink",
  "productSku",
  "type",
  "variantImageLink",
  "variantSku",
  "title",
  "consumerPrice",
  "importerrPrice",
  "orderQty",
  "importerrQty",
  "AP_RMB",
  "chargeableWeight",
  "LS",
  "localShipping",
  "consignPrice",
];

const getPricingModeForItem = (detail, order) => {
  const orderType =
    order?.marketplace?.toLowerCase() === "manual" || order?.createdVia === "manual_admin"
      ? order?.orderType?.toLowerCase()
      : null;
  if (orderType === "wholesale" || orderType === "dropshipping") {
    return orderType === "wholesale" ? "wholesale" : "dropshipping";
  }
  const wholesaleBreakdownMap = new Map();
  if (Array.isArray(order?.breakdown)) {
    for (const b of order.breakdown) {
      if (b?.pricingMode === "wholesale" && b?.productId && b?.breakdown?.[0]?.FBP) {
        wholesaleBreakdownMap.set(b.productId.toString(), true);
      }
    }
  }
  const productId =
    detail?.product?._id?.toString?.() || detail?.product?.toString?.() || "";
  if (!wholesaleBreakdownMap.has(productId)) return "dropshipping";
  const variant = detail?.variant;
  let variantPrice = 0;
  if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
    variantPrice = Number(variant.finalPrice);
  } else {
    const b = variant?.breakdown;
    variantPrice = Number(b?.FBP ?? b?.fbp) || 0;
  }
  const consumerPrice = Number(detail?.consumerPrice);
  if (variantPrice > 0 && Math.abs(variantPrice - consumerPrice) < 0.01) {
    return "dropshipping";
  }
  return "wholesale";
};

const getImporterrPricePerVariant = (variant, detail, order, pricingMode, qty = 1) => {
  const v = variant || detail?.variant;
  const isWholesale = pricingMode === "wholesale";
  let price = 0;
  if (isWholesale) {
    if (v?.wholesaleFinalPrice != null && Number(v.wholesaleFinalPrice) > 0) {
      price = Number(v.wholesaleFinalPrice);
    } else {
      const b = v?.breakdown;
      price = Number(b?.FBP ?? b?.fbp) || 0;
    }
  } else {
    if (v?.finalPrice != null && Number(v.finalPrice) > 0) {
      price = Number(v.finalPrice);
    } else {
      const b = v?.breakdown;
      price = Number(b?.FBP ?? b?.fbp) || Number(v?.ap) || 0;
    }
  }
  return price * (Number.isFinite(Number(qty)) ? Number(qty) : 1);
};

const findProductFromMap = (detail, variant, productMap) => {
  if (!productMap) return null;
  const keys = [
    variant?.skuId && String(variant.skuId),
    detail?.variant?.skuId && String(detail.variant.skuId),
    detail?.sku,
    detail?.ASIN,
    detail?.product?.sku,
    (detail?.product?._id ?? detail?.product)?.toString?.(),
  ].filter(Boolean);
  for (const k of keys) {
    if (productMap[k]) return productMap[k];
  }
  return null;
};

/**
 * Full sheet column order and labels (single row per variant).
 * Order matches user requirement: S.No through Remarks.
 */
export const FULL_SHEET_COLUMNS = [
  { key: "sNo", label: "S.No" },
  { key: "orderSourceMktName", label: "Order Source/MKT Name" },
  { key: "orderDate", label: "Order Date" },
  { key: "image", label: "Image" },
  { key: "imageLink", label: "Image Link" },
  { key: "impOrderId", label: "Imp Order ID" },
  { key: "mkpOrderId", label: "MKP Order ID" },
  { key: "orderItemId", label: "Order Item ID" },
  { key: "productName", label: "Product Name" },
  { key: "skuOfferId", label: "SKU/Offer ID" },
  { key: "variantSku", label: "Variant SKU" },
  { key: "variantName", label: "Variant Name" },
  { key: "quantity", label: "Quantity" },
  { key: "productUrl", label: "Product URL" },
  { key: "orderItemValue", label: "Order Item Value" },
  { key: "shippingMethod", label: "Shipping Method" },
  { key: "shippingCharges", label: "Shipping Charges" },
  { key: "orderType", label: "Order Type (Variant)" },
  { key: "paymentMode", label: "Payment Mode" },
  { key: "paymentStatus", label: "Payment Status" },
  { key: "sellerName", label: "Seller Name" },
  { key: "userId", label: "User ID" },
  { key: "recipientName", label: "Recipient Nme" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "pincode", label: "Pincode" },
  { key: "mobNo", label: "Mob. No." },
  { key: "emailId", label: "Email Id" },
  { key: "importerrPrice", label: "Importerr Price" },
  { key: "bcd", label: "BCD" },
  { key: "tax", label: "TAX" },
  { key: "hsn", label: "HSN" },
  { key: "stockStatus", label: "Stock Status" },
  { key: "chinaLink", label: "China Link" },
  { key: "specificationVariantNameChinese", label: "Specification/Variant Name(Chinese)" },
  { key: "unitChinaPrice", label: "Unit China Price" },
  { key: "localShipping", label: "Local Shipping" },
  { key: "chinaWeightGram", label: "China Weight in Gram" },
  { key: "chinaMoq", label: "china MOQ" },
  { key: "priceOfTotalQty", label: "Price of Total QTY" },
  { key: "chinaNo", label: "China No." },
  { key: "chinaOrderNo", label: "Chine Order No." },
  { key: "chinaStatus", label: "China Status" },
  { key: "maskId", label: "Mask Id" },
  { key: "categories", label: "Categories" },
  { key: "lastMileCourier", label: "Last Mile Courier" },
  { key: "lastMileTracking", label: "Last Mile Tracking" },
  { key: "pickupDate", label: "Pickup Date" },
  { key: "status", label: "Status" },
  { key: "transactionId", label: "Transaction ID" },
  { key: "remarks", label: "Remarks" },
];

/** @deprecated Use FULL_SHEET_COLUMNS. Kept for backward compatibility. */
export const ORDER_SHEET_COLUMN_ORDER = [
  "serialNumber", "upsaleoOrderId", "orderId", "orderItemIds", "sellerName",
  "recipientName", "marketplace", "customerEmail", "orderDate", "status",
  "fromFailedOrder", "mkpPaymentStatus", "importerPrice", "mkpTotalWithShipping",
  "shippingCharges", "location", "updatedAt",
];

const COLUMN_DEFS = {
  serialNumber: { label: "S.No.", format: "number" },
  upsaleoOrderId: { label: "Importerr Order Id", format: null },
  orderId: { label: "MKP Order ID", format: null },
  orderItemIds: { label: "Order Item IDs", format: null },
  sellerName: { label: "Seller Name", format: null },
  recipientName: { label: "Recipient Name", format: null },
  marketplace: { label: "MKP Name", format: null },
  customerEmail: { label: "Customer Email", format: null },
  orderDate: { label: "Order Date", format: "date" },
  status: { label: "Status", format: null },
  fromFailedOrder: { label: "From Failed Order", format: null },
  mkpPaymentStatus: { label: "MKP Payment Status", format: null },
  importerPrice: { label: "Importerr Price", format: "currency" },
  mkpTotalWithShipping: { label: "MKP Total Price", format: "currency" },
  shippingCharges: { label: "Shipping Charges", format: "currency" },
  location: { label: "Location", format: null },
  updatedAt: { label: "Updated Date", format: "date" },
};

const ROW_ITEM_LABELS = {
  productImageLink: "Product Image Link",
  productSku: "Product SKU",
  type: "Type",
  variantImageLink: "Variant Image Link",
  variantSku: "Variant SKU",
  title: "Title",
  consumerPrice: "Consumer Price",
  importerrPrice: "Importerr Price",
  orderQty: "Order Qty",
  importerrQty: "Importerr Qty",
  AP_RMB: "AP_RMB (Breakdown)",
  chargeableWeight: "Chargeable Weight (kg)",
  LS: "LS (Breakdown)",
  localShipping: "Local Shipping (Variant)",
  consignPrice: "Consign Price (Variant)",
};

/**
 * Export orders to Excel with one row per variant.
 * E.g. 1 order with 1 product and 3 variants = 3 rows (each row: same order + parent info, different variant info).
 * Parent product image and SKU are fetched from DB via productMap.
 * productMap: object keyed by sku/variantSkuId/asin, value { imgUrl, sku, title }.
 */
export const exportOrdersToSheetWithItems = async (orders, options = {}) => {
  const { getSellerName, getMkpPaymentStatus, columnOrder, productMap = {}, sellerMap = {} } = options;

  // Helper to get seller ID as string
  const getSellerIdStr = (order) => {
    const sid = order?.sellerId;
    if (!sid) return null;
    if (typeof sid === "object") return String(sid._id || sid.id || "");
    return String(sid);
  };

  // Helper to get seller uniqueId from sellerMap (fetched from User collection)
  const getSellerUniqueId = (order) => {
    const sidStr = getSellerIdStr(order);
    if (sidStr && sellerMap[sidStr]?.uniqueId) {
      return sellerMap[sidStr].uniqueId;
    }
    if (order?.sellerId?.uniqueId) return order.sellerId.uniqueId;
    return "";
  };

  // Helper to get seller email from sellerMap (fetched from User collection)
  const getSellerEmail = (order) => {
    const sidStr = getSellerIdStr(order);
    if (sidStr && sellerMap[sidStr]?.email) {
      return sellerMap[sidStr].email;
    }
    // Fallback to populated sellerId if available
    if (order?.sellerId?.email) return order.sellerId.email;
    return "";
  };

  const defaultGetSellerName = (order) => {
    const sidStr = getSellerIdStr(order);
    // Try sellerMap first
    if (sidStr && sellerMap[sidStr]) {
      const { firstName, lastName } = sellerMap[sidStr];
      const name = `${firstName || ""} ${lastName || ""}`.trim();
      if (name) return name;
    }
    // Fallback to populated sellerId
    const seller = order?.sellerId;
    if (!seller) return "-";
    return `${seller.firstName || ""} ${seller.lastName || ""}`.trim() || "-";
  };

  const defaultGetMkpPaymentStatus = (order) => {
    if (Array.isArray(order?.mkpOrderStatusHistory) && order.mkpOrderStatusHistory.length > 0) {
      const lastItem = order.mkpOrderStatusHistory[order.mkpOrderStatusHistory.length - 1];
      return lastItem?.mkp_order_status || order?.mkp_financial_status || "";
    }
    return order?.mkp_financial_status || "";
  };

  const sellerNameFn = getSellerName || defaultGetSellerName;
  const mkpStatusFn = getMkpPaymentStatus || defaultGetMkpPaymentStatus;

  const fullSheetColumns = FULL_SHEET_COLUMNS;
  const headers = fullSheetColumns.map((c) => c.label);

  const getOrderValues = (order, serialNo) => {
    const orderItemIds =
      order.orderDetails?.map((d) => d?.orderItemId).filter(Boolean) || [];
    const uniqueOrderItemIds = [...new Set(orderItemIds)];
    // Order details page shows Order Date from createdAt; use same for export
    const orderDate = (order?.createdAt || order?.orderDate) ? new Date(order.createdAt || order.orderDate) : null;
    const updatedAt = order?.updatedAt ? new Date(order.updatedAt) : null;
    const location = [order?.shippingState, order?.shippingPostalCode]
      .filter(Boolean)
      .join(" – ") || "-";
    return {
      serialNumber: serialNo,
      upsaleoOrderId: order?.upsaleoOrderId || "",
      orderId: order?.orderId || "",
      orderItemIds: uniqueOrderItemIds.join(", "),
      sellerName: sellerNameFn(order),
      recipientName: order?.recipientName || "",
      marketplace: order?.marketplace || "",
      customerEmail: order?.customerEmail || order?.user?.email || "",
      orderDate,
      status: order?.status || "",
      fromFailedOrder: order?.fromFailedOrder ? "Yes" : "No",
      mkpPaymentStatus: mkpStatusFn(order),
      importerPrice: Number(order?.importerPrice) ?? Number(order?.totalPrice) ?? 0,
      mkpTotalWithShipping:
        Number(order?.mkpTotalWithShipping) ?? Number(order?.mkp_current_total_price) ?? 0,
      shippingCharges:
        Number(order?.shippingCharges) ?? Number(order?.totalShippingCharges) ?? 0,
      location,
      updatedAt,
    };
  };

  const buildFullSheetRow = (order, detail, v, serialNo, productInfo, pricingMode) => {
    const typeDisplay = pricingMode === "wholesale" ? "Wholesale" : "Dropshipping";
    const parentImage =
      (Array.isArray(productInfo?.imgUrl) ? productInfo?.imgUrl?.[0] : productInfo?.imgUrl) ||
      productInfo?.image ||
      detail?.image ||
      detail?.product?.imgUrl?.[0] ||
      "";
    const parentSku =
      productInfo?.sku ||
      productInfo?.offerId ||
      detail?.product?.sku ||
      detail?.sku ||
      detail?.orderSku ||
      "";
    const variantImage =
      v?.skuAttributes?.[0]?.skuImageUrl ||
      v?.skuImageUrl ||
      v?.image ||
      detail?.variant?.skuImageUrl ||
      detail?.variant?.skuAttributes?.[0]?.skuImageUrl ||
      detail?.image ||
      "";
    const variantSku =
      v?.skuId?.toString?.() || v?.sku || detail?.variant?.skuId?.toString?.() || detail?.sku || detail?.ASIN || "";
    const productName =
      productInfo?.title ||
      detail?.title ||
      detail?.product?.title ||
      detail?.productTitle ||
      detail?.variant?.title ||
      "";
    const qty = Number(v?.quantity) ?? Number(detail?.quantity) ?? 1;
    const consumerPrice = Number(detail?.consumerPrice) || 0;
    const orderItemValue = consumerPrice * qty;
    const importerrPrice = getImporterrPricePerVariant(v, detail, order, pricingMode, qty);
    const breakdown = v?.breakdown || detail?.variant?.breakdown || {};
    const apRmb = (() => {
      if (pricingMode === "dropshipping") {
        const variantAp = v?.ap ?? detail?.variant?.ap;
        if (variantAp != null) return Number(variantAp);
      }
      return breakdown.AP_RMB != null ? Number(breakdown.AP_RMB) : breakdown.AP != null ? Number(breakdown.AP) : "";
    })();
    const consignPriceVal =
      v?.consignPrice != null ? Number(v.consignPrice)
      : v?.cp != null ? Number(v.cp)
      : v?.fenxiaoPriceInfo?.consignPrice != null ? Number(v.fenxiaoPriceInfo.consignPrice)
      : detail?.variant?.consignPrice != null ? Number(detail.variant.consignPrice)
      : detail?.variant?.cp != null ? Number(detail.variant.cp)
      : breakdown?.CP != null ? Number(breakdown.CP)
      : breakdown?.cp != null ? Number(breakdown.cp)
      : "";
    const chargeableWeightKg =
      v?.chargeableWeight != null ? Number(v.chargeableWeight)
      : v?.fenxiaoPriceInfo?.chargeableWeight != null ? Number(v.fenxiaoPriceInfo.chargeableWeight)
      : detail?.variant?.chargeableWeight != null ? Number(detail.variant.chargeableWeight)
      : breakdown.chargeableWeight != null ? Number(breakdown.chargeableWeight)
      : "";
    const chinaWeightGram = chargeableWeightKg !== "" && Number.isFinite(chargeableWeightKg) ? chargeableWeightKg * 1000 : "";
    const lsVal = (() => {
      const variantLs = v?.ls ?? detail?.variant?.ls;
      if (variantLs != null) return Number(variantLs);
      const attrs = v?.skuAttributes || detail?.variant?.skuAttributes || [];
      const attrWithLs = Array.isArray(attrs) ? attrs.find((a) => a?.ls != null) : null;
      if (attrWithLs?.ls != null) return Number(attrWithLs.ls);
      return breakdown.LS != null ? Number(breakdown.LS) : breakdown.ls != null ? Number(breakdown.ls) : "";
    })();
    const orderDate = (order?.createdAt || order?.orderDate) ? new Date(order.createdAt || order.orderDate) : null;
    // HSN/BCD/TAX from order.hsnInfo (from hsn_mappings when order was created/updated)
    const hsnInfo = order?.hsnInfo || {};
    const bcd = hsnInfo.BCD != null ? Number(hsnInfo.BCD) : (hsnInfo.FinalCustomDuty != null ? Number(hsnInfo.FinalCustomDuty) : "");
    const tax = hsnInfo.gstRate != null ? Number(hsnInfo.gstRate) : (hsnInfo.igstRate != null ? Number(hsnInfo.igstRate) : (hsnInfo.IGST != null ? Number(hsnInfo.IGST) * 100 : ""));
    const hsn = hsnInfo.HSN != null ? String(hsnInfo.HSN) : (hsnInfo.hsnCode || "");
    const address = [
      order?.shippingAddress1,
      order?.shippingAddress2,
      order?.shippingAddress3,
    ].filter(Boolean).join(", ") || "";
    const paymentStatus = mkpStatusFn(order);
    const shippingCharges = Number(order?.shippingCharges) ?? Number(order?.totalShippingCharges) ?? 0;
    const priceOfTotalQty = "";
    const firstDetail = order?.orderDetails?.[0];
    const lastMileCourier = detail?.carrierName ?? firstDetail?.carrierName ?? "";
    const lastMileTracking = detail?.trackingId ?? firstDetail?.trackingId ?? "";
    const pickupDate = order?.shipDate || order?.processingDate ? new Date(order.shipDate || order.processingDate) : null;

    const isShopify = String(order?.marketplace || "").toLowerCase() === "shopify";
    const mkpOrderIdVal = order?.orderId || "";
    const orderItemIdVal = detail?.orderItemId || "";
    const mkpOrderId = isShopify ? orderItemIdVal : mkpOrderIdVal;
    const orderItemId = isShopify ? mkpOrderIdVal : orderItemIdVal;

    return {
      sNo: serialNo,
      orderType: typeDisplay,
      orderSourceMktName: order?.marketplace || "",
      orderDate,
      image: "",
      imageLink: variantImage || parentImage || "",
      impOrderId: order?.upsaleoOrderId || "",
      mkpOrderId,
      orderItemId,
      productName,
      skuOfferId: parentSku,
      variantSku,
      variantName: (() => {
        const attrs = v?.skuAttributes || detail?.variant?.skuAttributes || detail?.skuAttributes || [];
        if (!Array.isArray(attrs) || attrs.length === 0) return "";
        return attrs
          .map((a) => a?.valueTrans || a?.value || "")
          .filter(Boolean)
          .join("; ") || "";
      })(),
      quantity: qty,
      productUrl: (parentSku && variantSku && productName ? `https://www.importerr.com/${parentSku}/${variantSku}/${slugifyTitle(productName)}` : "") || detail?.productUrl || "",
      orderItemValue,
      importerrPrice,
      bcd,
      tax,
      hsn,
      shippingMethod: order?.shippingMethod || detail?.shippingMethod || "",
      shippingCharges,
      paymentMode: order?.paymentMode || "",
      paymentStatus,
      sellerName: sellerNameFn(order),
      userId: getSellerUniqueId(order),
      sellerEmail: getSellerEmail(order),
      recipientName: order?.recipientName || "",
      address,
      city: order?.shippingCity || "",
      state: order?.shippingState || "",
      pincode: order?.shippingPostalCode || "",
      mobNo: order?.shippingPhoneNumber || "",
      emailId: order?.customerEmail || order?.user?.email || "",
      stockStatus: "",
      chinaLink: v?.chinaLink || detail?.chinaLink || "",
      specificationVariantNameChinese: "",
      unitChinaPrice: pricingMode === "wholesale"
        ? (consignPriceVal !== "" ? consignPriceVal : "")
        : (apRmb !== "" ? apRmb : ""),
      localShipping: lsVal !== "" ? lsVal : "",
      chinaWeightGram,
      chinaMoq: "",
      priceOfTotalQty,
      chinaNo: detail?.chinaNo || order?.chinaNo || "",
      chinaOrderNo: v?.supplierOrderId || detail?.variant?.supplierOrderId || detail?.chinaOrderNo || order?.chinaOrderNo || "",
      chinaStatus: detail?.chinaStatus || order?.chinaStatus || "",
      maskId: detail?.maskId || order?.maskId || "",
      categories: (hsnInfo.category || hsnInfo.Item || "").trim() || productInfo?.categories || detail?.categories || "",
      lastMileCourier,
      lastMileTracking,
      pickupDate,
      status: order?.status || "",
      transactionId: order?.razorpayPaymentId || order?.transactionId?.paymentId || order?.transactionId?.transactionId || "",
      remarks: order?.remarks || detail?.remarks || order?.customerNote || "",
    };
  };

  const expandOrdersToVariantRows = (ordersList) => {
    const rows = [];
    let serialNo = 1;
    for (const order of ordersList) {
      const details = order?.orderDetails || [];
      for (const detail of details) {
        if (detail?.isFailed) continue;

        const pricingMode = getPricingModeForItem(detail, order);
        const variants = Array.isArray(detail.variants) && detail.variants.length > 0
          ? detail.variants
          : detail.variant
            ? [{ ...detail.variant, quantity: detail.quantity }]
            : [{ quantity: detail.quantity }];

        for (const v of variants) {
          const productInfo = findProductFromMap(detail, v, productMap);
          const breakdown = v?.breakdown || detail?.variant?.breakdown || {};
          const fullRow = buildFullSheetRow(order, detail, v, serialNo, productInfo, pricingMode);

          rows.push({
            fullRow,
            orderValues: getOrderValues(order, serialNo),
            itemRow: {
              productImageLink: fullRow.imageLink || fullRow.image,
              productSku: fullRow.skuOfferId,
              type: fullRow.orderType,
              variantImageLink: fullRow.imageLink,
              variantSku: fullRow.variantSku,
              title: fullRow.productName,
              consumerPrice: fullRow.orderItemValue / (fullRow.quantity || 1),
              importerrPrice: fullRow.importerrPrice,
              orderQty: fullRow.quantity,
              importerrQty: fullRow.quantity,
              AP_RMB: fullRow.unitChinaPrice,
              chargeableWeight: fullRow.chinaWeightGram !== "" ? fullRow.chinaWeightGram / 1000 : "",
              LS: breakdown.LS != null ? Number(breakdown.LS) : "",
              localShipping: fullRow.localShipping,
              consignPrice: "",
            },
            rawBreakdown: breakdown && typeof breakdown === "object" ? { ...breakdown } : {},
            rawVariant: v && typeof v === "object" ? { ...v } : {},
            rawOrderBreakdown: Array.isArray(order?.breakdown) ? order.breakdown : [],
            detail,
            orderRef: order,
          });
          serialNo += 1;
        }
      }
    }
    return rows;
  };

  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Orders");

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4472C4" },
      };
      cell.font = {
        color: { argb: "FFFFFFFF" },
        bold: true,
        size: 11,
      };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    const ordersList = Array.isArray(orders) ? orders : [];
    const flatRows = expandOrdersToVariantRows(ordersList);

    const dateKeys = ["orderDate", "pickupDate"];
    const currencyKeys = ["orderItemValue", "importerrPrice", "shippingCharges", "priceOfTotalQty"];

    flatRows.forEach(({ fullRow }, rowIndex) => {
      const rowData = fullSheetColumns.map((c) => {
        const val = fullRow[c.key];
        if (val == null) return "";
        if (dateKeys.includes(c.key) && val instanceof Date) {
          const d = val;
          if (isNaN(d.getTime())) return "";
          return d;
        }
        return val;
      });
      const dataRow = worksheet.addRow(rowData);

      dataRow.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (rowIndex % 2 === 0) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF8F9FA" },
          };
        }
      });

      fullSheetColumns.forEach((col, colIndex) => {
        const excelCol = colIndex + 1;
        const cell = dataRow.getCell(excelCol);
        if (dateKeys.includes(col.key) && cell.value) {
          cell.numFmt = "dd/mm/yyyy hh:mm";
        }
        if (currencyKeys.includes(col.key) && cell.value != null && cell.value !== "") {
          cell.numFmt = "₹#,##0.00";
        }
        if (col.key === "unitChinaPrice" || col.key === "localShipping") {
          if (cell.value != null && cell.value !== "") cell.numFmt = "#,##0.00";
        }
        if (col.key === "chinaWeightGram" && cell.value != null && cell.value !== "") {
          cell.numFmt = "0";
        }
      });
    });

    const orderDateColIdx = fullSheetColumns.findIndex((c) => c.key === "orderDate");
    const pickupDateColIdx = fullSheetColumns.findIndex((c) => c.key === "pickupDate");
    worksheet.columns.forEach((col, i) => {
      if (col) {
        let w = Math.min(Math.max(headers[i]?.length || 10, 12), 50);
        if (i === orderDateColIdx || i === pickupDateColIdx) {
          w = Math.max(w, 18);
        }
        col.width = w;
      }
    });

    // --- Raw Data sheet: same rows with full breakdown and variant raw data ---
    const RAW_BREAKDOWN_KEYS = [
      "AP_RMB", "AP", "AP_INR", "chargeableWeight", "LS", "FBP", "MP", "CP", "CD",
      "IF", "LMD", "LC", "PF", "GST_PR", "GST_FR", "GST_PF", "markupPercentage",
    ];
    const RAW_VARIANT_KEYS = ["localShipping", "consignPrice", "chargeableWeight", "wholesaleFinalPrice", "finalPrice", "cp", "ap", "ls"];
    const rawHeaders = [
      "S.No.",
      "Importerr Order Id",
      "Order Id",
      "Variant SKU",
      "Product SKU",
      ...RAW_BREAKDOWN_KEYS,
      ...RAW_VARIANT_KEYS,
      "Raw Breakdown (JSON)",
      "Raw Variant (JSON)",
      "Order Breakdown (JSON)",
    ];
    const rawSheet = workbook.addWorksheet("Raw Data", { state: "visible" });

    const rawHeaderRow = rawSheet.addRow(rawHeaders);
    rawHeaderRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E7D32" } };
      cell.font = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    });

    flatRows.forEach(({ orderValues, itemRow, rawBreakdown, rawVariant, rawOrderBreakdown }, rowIndex) => {
      const breakdownCells = RAW_BREAKDOWN_KEYS.map((k) => {
        const val = rawBreakdown[k];
        if (val == null) return "";
        if (typeof val === "number") return val;
        return Number(val) || val;
      });
      const variantCells = RAW_VARIANT_KEYS.map((k) => {
        const val = rawVariant[k] ?? rawVariant?.fenxiaoPriceInfo?.[k];
        if (val == null) return "";
        if (typeof val === "number") return val;
        return Number(val) || val;
      });
      const rawBreakdownStr =
        Object.keys(rawBreakdown || {}).length > 0
          ? JSON.stringify(rawBreakdown)
          : "";
      const rawVariantStr =
        Object.keys(rawVariant || {}).length > 0
          ? JSON.stringify(rawVariant)
          : "";
      const rawOrderBreakdownStr =
        Array.isArray(rawOrderBreakdown) && rawOrderBreakdown.length > 0
          ? JSON.stringify(rawOrderBreakdown)
          : "";

      const rawRowData = [
        orderValues.serialNumber,
        orderValues.upsaleoOrderId,
        orderValues.orderId,
        itemRow.variantSku ?? "",
        itemRow.productSku ?? "",
        ...breakdownCells,
        ...variantCells,
        rawBreakdownStr,
        rawVariantStr,
        rawOrderBreakdownStr,
      ];
      const rawDataRow = rawSheet.addRow(rawRowData);

      rawDataRow.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
        if (rowIndex % 2 === 0) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F8E9" } };
        }
      });

      const orderListColCount = 5;
      RAW_BREAKDOWN_KEYS.forEach((key, idx) => {
        const colIndex = orderListColCount + idx + 1;
        const cell = rawDataRow.getCell(colIndex);
        if (cell.value != null && cell.value !== "") {
          if (key === "chargeableWeight") {
            cell.numFmt = "0.000";
          } else {
            cell.numFmt = "#,##0.00";
          }
        }
      });
      RAW_VARIANT_KEYS.forEach((key, idx) => {
        const colIndex = orderListColCount + RAW_BREAKDOWN_KEYS.length + idx + 1;
        const cell = rawDataRow.getCell(colIndex);
        if (cell.value != null && cell.value !== "") {
          if (key === "chargeableWeight") {
            cell.numFmt = "0.000";
          } else {
            cell.numFmt = "#,##0.00";
          }
        }
      });
    });

    rawSheet.columns.forEach((col, i) => {
      if (col) {
        col.width = i >= rawHeaders.length - 3 ? 40 : Math.min(Math.max(rawHeaders[i]?.length || 10, 12), 25);
      }
    });

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const finalFilename = `orders-sheet-${timestamp}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", finalFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename: finalFilename, rowCount: flatRows.length };
  } catch (error) {
    console.error("Export orders to sheet error:", error);
    return { success: false, error: error?.message };
  }
};